import {
  Store,
  LayoutTemplate,
  Package,
  Layers,
  ClipboardList,
  FileText,
  FlaskConical,
  Users,
  Undo2,
  FileBarChart,
  type LucideIcon,
} from "lucide-react";

export interface StoreNavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /**
   * Permission required to see this item (ADR-0030). Absent = always visible.
   * `StoreAdminLayout` filters on it and drops groups left empty. The server
   * gate is the floor — this is display-only.
   */
  permission?: string;
}

export interface StoreNavGroup {
  /** Section heading; `null` for the leading ungrouped item. */
  label: string | null;
  ariaLabel: string;
  items: StoreNavItem[];
}

/**
 * The Store admin sidebar — grouping and order mirror the wireframe deck's
 * sidebar (`prototype/store/admin/all.html`): Dashboard, then Catalog, Sales,
 * After-sales, Insights.
 *
 * Rendered by `StoreAdminLayout` as a left rail inside the page, because the
 * workspace chrome itself is a topbar and has no global sidebar.
 *
 * The deck's Docs group (Database, Schema ERD, FK Connections, Admin Flow) is
 * intentionally omitted — those pages document the schema for reviewers and
 * aren't product surfaces. Discounts and Buyer Feedback were dropped from the
 * Store scope.
 */
export const STORE_NAV: StoreNavGroup[] = [
  {
    label: null,
    ariaLabel: "Store",
    items: [{ to: "/store/admin", label: "Dashboard", icon: Store }],
  },
  {
    label: "Catalog",
    ariaLabel: "Store catalog",
    items: [
      {
        to: "/store/admin/products",
        label: "Products",
        icon: Package,
        permission: "store.catalog.view",
      },
      // Packs (`/store/admin/packs`) is deliberately absent (#248). Since #247
      // removed the pack picker from the pricing dialog, every price is composed
      // in the Bundle Builder and the library is no longer a surface anyone
      // prices from — so the rail stopped advertising it. The route still
      // resolves and the page still works: this hides it, it does not remove it.
      // TODO(2026-11-10): decide and act — either re-add this item, or delete
      // `features/store/packs/` plus the route for good. Don't let it sit.
      {
        to: "/store/admin/collections",
        label: "Collections",
        icon: Layers,
        permission: "store.catalog.view",
      },
    ],
  },
  {
    label: "Storefront",
    ariaLabel: "Storefront",
    items: [
      {
        to: "/store/admin/storefront",
        label: "Appearance",
        icon: LayoutTemplate,
        permission: "store.catalog.view",
      },
      {
        to: "/store/admin/pages",
        label: "Pages",
        icon: FileText,
        permission: "store.catalog.view",
      },
    ],
  },
  {
    label: "Sales",
    ariaLabel: "Store sales",
    items: [
      {
        to: "/store/admin/orders",
        label: "Orders",
        icon: ClipboardList,
        permission: "store.orders.view",
      },
      {
        to: "/store/admin/quotes",
        label: "Quotes",
        icon: FileText,
        permission: "store.quotes.view",
      },
      { to: "/store/admin/sampling", label: "Sampling", icon: FlaskConical },
      { to: "/store/admin/buyers", label: "Buyers", icon: Users },
    ],
  },
  {
    label: "After-sales",
    ariaLabel: "Store after-sales",
    items: [{ to: "/store/admin/returns", label: "Returns", icon: Undo2 }],
  },
  {
    label: "Insights",
    ariaLabel: "Store insights",
    // Audit log is deliberately absent (#249, ADR-0062). The store rail used to
    // surface the workspace-wide log at `/records/audit-log` — the store has no
    // separate one — and it was the rail's only cross-module link. It went from
    // both rails at once so the two can't disagree about whether the log is
    // discoverable; the page, its route and its `audit.view` gate are untouched,
    // and specific entries still deep-link from the surfaces that raise them.
    items: [{ to: "/store/admin/reports", label: "Reports", icon: FileBarChart }],
  },
];
